/**
 * Settings → Models (models.md §12; web.md §4 Models row, fixes 6 and 7):
 *   - every model of the catalogue in model order (Jev 1.13, Multilingual E5 Small) with its two measured
 *     numbers, the accuracy and speed bars and its state on this device, and the card "Use the
 *     recommended set-up" when the set-up differs from it (ModelsList.tsx);
 *   - each model's editor (ModelEditor.tsx): the switch, overview, download block, run rule, Test, its
 *     actions on scoring, options, order;
 *   - then every scoring rule in evaluation order under "Actions on scoring", each marked with the model
 *     it tests, in the shared RuleEditor (score fields naming their model, no trigger);
 *   - "Written assessments": the connected assistant over MCP.
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import type { DialogProps } from '../../app/dialogs.js';
import { DialogFrame, isDialogRegistered, registerDialog } from '../../app/dialogs.js';
import { reportError } from '../../app/errors.js';
import { useEngine, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import { Section } from '../../ui/form.js';
import type { ModelCatalogEntry, RuleId, ScoringRule } from '@digester/core';
import { useScoringRules, useTags, useTopics } from '../rules/data.js';
import { modelsOfCondition, newRuleDraft, ruleSummary, testsShownScores, toDraft, type RuleDraft } from '../rules/model.js';
import { ArchivedNote, OrderSection } from '../rules/OrderSection.js';
import { RuleEditor } from '../rules/RuleEditor.js';
import { saveRuleDraft } from '../rules/save.js';
import { isLadderRule, orderWithNewRule, sameOrder, topicRowOf } from '../rules/scoring.js';
import '../rules/rules.css';
import { EditorEmpty, EditorFrame, useDraft, type ItemsResult, type SettingsCategory, type SettingsEditorProps, type SettingsItem } from '../shell/index.js';
import { ASSISTANT, modelStatus, qualityLine, whereLine } from './catalog.js';
import { entryOf, shortModelName, useCatalog, useInstalls, useModels } from './data.js';
import { ModelEditor } from './ModelEditor.js';
import { ModelsList } from './ModelsList.js';
import './models.css';

function useItems(): ItemsResult {
  const { rules, isLoading } = useScoringRules();
  const { models, isLoading: modelsLoading, error } = useModels();
  const catalog = useCatalog();
  const installs = useInstalls();
  const pipeline = useEngineQuery(qk.pipeline(), (e) => e.pipeline.status()).data;
  const modelItems: SettingsItem[] = models.map((m) => {
    const entry = entryOf(catalog, m.id);
    const wait = pipeline?.models.find((s) => s.modelId === m.id)?.waiting ?? null;
    const status = modelStatus(m, entry, installs.byId.get(m.id), installs.unavailable, wait);
    return {
      id: m.id,
      name: m.name,
      icon: m.icon,
      subtitle: entry ? `${qualityLine(entry)}\n${whereLine(entry)}` : m.kind,
      status: status.label,
      enabled: status.tone === 'on',
      ...(status.tone === 'error' ? { tone: 'error' as const } : status.tone === 'warn' ? { tone: 'warn' as const } : {}),
      builtin: true,
      // One record per catalogue entry (models.md §2): a model is never created, duplicated or deleted.
      duplicable: false,
    };
  });
  const assistant: SettingsItem = { id: ASSISTANT.id, name: ASSISTANT.name, icon: 'brain', subtitle: ASSISTANT.subtitle, builtin: true, duplicable: false, groupHeading: 'Written assessments' };
  const ruleItems: SettingsItem[] = rules.map((r, i) => {
    const named = modelsOfCondition(r.condition).map((id) => shortModelName(id, models, catalog));
    const tested = named.length ? named.join(' + ') : testsShownScores(r.condition) ? 'Shown scores' : '';
    const what = isLadderRule(r.id) ? `Ladder · ${ruleSummary(r)}` : topicRowOf(r) ? 'Topic rule' : ruleSummary(r);
    return {
      id: r.id,
      name: r.name,
      icon: r.icon,
      subtitle: tested ? `${tested} · ${what}` : what,
      enabled: r.enabled,
      ...(r.archivedAt !== null ? { status: 'Archived' } : {}),
      ...(i === 0 ? { groupHeading: 'Actions on scoring' } : {}),
    };
  });
  return { items: [...modelItems, ...ruleItems, assistant], isLoading: isLoading || modelsLoading, error };
}

function ModelsEditor({ recordId, duplicateOf }: SettingsEditorProps) {
  const { rules, isLoading } = useScoringRules();
  const { models, isLoading: modelsLoading, error } = useModels();
  const catalog = useCatalog();
  const installs = useInstalls();
  const tags = useTags();
  const [search] = useSearchParams();
  if (!recordId) return <EditorEmpty />;
  if (recordId === ASSISTANT.id) return <AssistantEditor />;
  const model = models.find((m) => m.id === recordId);
  if (model) {
    const entry = entryOf(catalog, model.id);
    if (!entry) return <EditorEmpty text="Loading…" />;
    return <ModelEditor key={model.id} model={model} entry={entry} models={models} rules={rules} install={installs.byId.get(model.id)} unavailable={installs.unavailable} />;
  }
  if (isLoading || modelsLoading) return <EditorEmpty text="Loading…" />;
  if (error && recordId !== 'new' && !rules.some((r) => r.id === recordId)) return <EditorEmpty text={error.message} />;
  if (recordId === 'new') {
    const from = duplicateOf ? rules.find((r) => r.id === duplicateOf) : undefined;
    const forModel = search.get('model') ?? models.find((m) => m.enabled && m.entitled)?.id ?? null;
    const initial: RuleDraft = from ? { ...toDraft(from, tags), name: `${from.name} copy`.slice(0, 80) } : newRuleDraft('scoring', null, forModel);
    return <ScoringRuleForm rule={null} initial={initial} rules={rules} />;
  }
  const rule = rules.find((r) => r.id === recordId);
  if (!rule) return <EditorEmpty />;
  return <ScoringRuleForm rule={rule} initial={toDraft(rule, tags)} rules={rules} />;
}

// ── Scoring rules ────────────────────────────────────────────────────────────────────────────────

function ScoringRuleForm({ rule, initial, rules }: { rule: ScoringRule | null; initial: RuleDraft; rules: readonly ScoringRule[] }) {
  const engine = useEngine();
  const navigate = useNavigate();
  const tags = useTags();
  const { models } = useModels();
  const catalog = useCatalog();
  const draft = useDraft<RuleDraft>({
    initial,
    isNew: rule === null,
    onSave: async (v) => {
      const id = await saveRuleDraft(engine, rule, v, tags);
      if (rule === null) {
        // A new rule acts only above the ladder's catch-all rows: place it above its model's block.
        const live = (await engine.rules.list('scoring')).filter((r) => r.archivedAt === null);
        const saved = live.find((r) => r.id === id);
        const order = orderWithNewRule(live, id, saved ? modelsOfCondition(saved.condition) : []);
        if (!sameOrder(order, live.map((r) => r.id))) await engine.rules.reorder('scoring', order);
      }
      return id;
    },
  });
  const v = draft.value;
  const archived = rule !== null && rule.archivedAt !== null;
  const row = rule ? topicRowOf(rule) : null;
  const allTopics = useTopics();
  const topics = row ? allTopics.find((t) => t.id === row.topicId) ?? null : null;
  const restore = async (): Promise<void> => {
    if (!rule) return;
    try {
      await engine.rules.restore(rule.id as RuleId);
    } catch (error) {
      reportError(error);
    }
  };
  const tested = modelsOfCondition(rule?.condition ?? { type: 'group', operator: 'all', children: [] });
  const note = topics ? (
    <div className="editor-note topic-row-note">
      This rule belongs to the topic <strong>{topics.name}</strong>: it is also its row “Score ≥ {row?.min}” in{' '}
      <button type="button" className="text-link" onClick={() => navigate(`/settings/topics/${encodeURIComponent(topics.id)}`)}>
        Topics → {topics.name}
      </button>
      .
    </div>
  ) : rule && isLadderRule(rule.id) ? (
    <div className="editor-note topic-row-note">
      Part of the seeded ladder of{' '}
      {tested.map((id, i) => (
        <span key={id}>
          {i > 0 ? ' and ' : ''}
          <button type="button" className="text-link" onClick={() => navigate(`/settings/models/${encodeURIComponent(id)}`)}>
            {shortModelName(id, models, catalog) === 'On-device' ? models.find((m) => m.id === id)?.name ?? id : shortModelName(id, models, catalog)}
          </button>
        </span>
      ))}
      . “Reset ladder” in a model’s settings restores its default values.
    </div>
  ) : null;
  return (
    <EditorFrame
      title={v.name.trim() || rule?.name || 'New scoring rule'}
      icon={v.icon}
      {...(archived ? { status: { label: 'Archived', on: false } } : { enabled: { checked: v.enabled, onChange: (enabled: boolean) => draft.set((d) => ({ ...d, enabled })) } })}
    >
      {archived ? <ArchivedNote onRestore={() => void restore()} /> : null}
      <RuleEditor value={v} set={draft.set} ruleId={archived ? null : (rule?.id ?? null)} dirty={draft.dirty} note={note}>
        {rule && !archived ? (
          <OrderSection
            kind="scoring"
            rule={rule}
            rules={rules}
            help="Runs when a model’s score arrives for a message that only its source or scoring rules have placed. Top to bottom; the first rule that matches acts. A rule naming a model whose score is still pending waits for it."
          />
        ) : null}
      </RuleEditor>
    </EditorFrame>
  );
}

// ── Connected assistant ──────────────────────────────────────────────────────────────────────────

function AssistantEditor() {
  const navigate = useNavigate();
  return (
    <EditorFrame title={ASSISTANT.name} icon="brain" status={{ label: 'Over MCP', on: true }}>
      <Section title="Assessment">
        <p className="model-note-row">
          Written assessments and briefings come from your own assistant — Claude, ChatGPT or another MCP client — connected to Digester over MCP. Digester runs no
          language model of its own and pays for none.
        </p>
        <div className="read-only-row">
          <span>Runs through</span>
          <button type="button" className="text-link" onClick={() => navigate('/settings/mcps')}>
            Your MCP connections <Icon name="external-link" />
          </button>
        </div>
        <div className="read-only-row">
          <span>Writes</span>
          <strong>An assessment on a message, a saved digest</strong>
        </div>
        <p className="field-help">Topic scores come from the scoring models above; assessments are a separate job and never move messages.</p>
      </Section>
    </EditorFrame>
  );
}

// ── Languages dialog (the prototype's `languages()`) ─────────────────────────────────────────────

function LanguagesDialog({ close, dialogId, entry }: DialogProps & { entry: ModelCatalogEntry }) {
  return (
    <DialogFrame title={`${entry.name} · languages`} onClose={close} dialogId={dialogId}>
      <p className="model-language-description">{entry.languages.note}</p>
      {entry.languages.examples.length > 1 ? <p className="field-help">Examples of supported languages</p> : null}
      <div className="model-language-grid">
        {entry.languages.examples.map((l) => (
          <span key={l}>{l}</span>
        ))}
      </div>
    </DialogFrame>
  );
}

if (!isDialogRegistered('model-languages')) registerDialog<{ entry: ModelCatalogEntry }>('model-languages', LanguagesDialog);

export const category: SettingsCategory = {
  id: 'models',
  name: 'Models',
  icon: 'brain',
  useItems,
  Editor: ModelsEditor,
  List: ModelsList,
  newLabel: 'New scoring rule',
  duplicate: true,
  archive: (engine, item) => engine.rules.archive(item.id as RuleId),
  archiveNote: 'Messages it already moved stay where they are. “Reset ladder” restores the seeded rules.',
};
